import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
  approveImportBill,
  deleteImportBill,
  forceDeleteImportBill,
  getImportBillById,
  getImportBillPaymentsById,
  importBillSendingForApproval,
  updateImportBillPayments,
} from "../../../services/Import";
import {
  Grid,
  Paper,
  Typography,
  Divider,
  Box,
  Tooltip,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  useTheme,
  alpha,
  Badge,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemSecondary,
  ListItemButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import TableItem from "../../../components/TableItem";

// Icons
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StoreIcon from "@mui/icons-material/Store";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import DiscountIcon from "@mui/icons-material/Discount";
import TagIcon from "@mui/icons-material/Tag";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import SendIcon from "@mui/icons-material/Send";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import PaymentList from "../../../components/PaymentList";
import AddPaymentIcon from "@mui/icons-material/RequestQuoteOutlined";
import PaymentOptions from "../../../components/PaymentOptions";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PaymentListForConfimation from "../../../components/PaymentListForConfimation";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

const SelectButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  minWidth: "100%",
  justifyContent: "flex-start",
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.2),
    borderColor: theme.palette.primary.main,
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.08)",
  marginLeft: theme.spacing(1),
}));

function Im_BillView() {
  const theme = useTheme();
  const history = useHistory();
  const { billId } = useParams();
  const [importBill, setImportBill] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [returnBillList, setReturnBillList] = useState([]);
  const [payments, setPayments] = useState([]);
  const [enablePayment, setEnablePayment] = useState(false);
  const [forceDeleteOpen, setForceDeleteOpen] = useState(false);
  const [forceDeleteConfirmText, setForceDeleteConfirmText] = useState("");
  const [forceDeleteLoading, setForceDeleteLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getImportBillById(billId);
      const paymentsResult = await getImportBillPaymentsById(billId);
      if (result.bill_id) {
        setImportBill(result);
        const billItemSet = result.import_billItems || [];

        const itemRows = billItemSet.map((item) => ({
          productCode: item.imports.product_code,
          item: item.imports.itemName,
          brand: item.imports.brand,
          qty: item.bill_qty,
          returnedQty: item.returnedQty,
          billUnit: item.billUnit,
          unitType: item.imports.unitType,
          itemUintsPerBillUnit: item.prodUnitAmountPerBillUnit,
          discountPerItem: item.discount_perItem,
          pricePerItem: item.pricePerItem,
          totalPrice: item.price,
          isPriceUpdate: item.isPriceUpdate ? "Yes" : "No",
        }));
        setReturnBillList(result.importReturnBills);
        setRows(itemRows);
      } else {
        setErrors({
          ...errors,
          _billDataError: "Unable to fetch bill data",
        });
      }

      if (paymentsResult !== "Something went wrong...") {
        setPayments(paymentsResult);
      } else {
        setErrors({
          ...errors,
          _billPaymentError: "Unable to fetch bill payment data",
        });
      }
    } catch (error) {
      console.error("Error fetching bill data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [billId]);

  const columns = [
    { id: "productCode", label: "Product Code", minWidth: 80 },
    { id: "item", label: "Item", minWidth: 150 },
    { id: "brand", label: "Brand", minWidth: 120 },
    {
      id: "qty",
      label: "Qty",
      minWidth: 80,
      type: "number",
      isDecimal: false,
    },
    {
      id: "returnedQty",
      label: "Returned Qty",
      minWidth: 80,
      type: "number",
      isDecimal: false,
    },
    { id: "billUnit", label: "Bill Unit", minWidth: 100, editable: true },
    { id: "unitType", label: "Unit", minWidth: 100, editable: false },
    {
      id: "itemUintsPerBillUnit",
      label: "Item Units Amount",
      minWidth: 100,
      editable: true,
      type: "number",
      isDecimal: true,
    },
    {
      id: "discountPerItem",
      label: "Discount",
      minWidth: 100,
      type: "number",
      isDecimal: false,
      format: (value) => `Rs: ${parseFloat(value || 0).toFixed(2)}`,
    },
    {
      id: "pricePerItem",
      label: "Price Per Item",
      minWidth: 120,
      type: "number",
      isDecimal: false,
      format: (value) => `Rs: ${parseFloat(value || 0).toFixed(2)}`,
    },
    {
      id: "totalPrice",
      label: "Total Price",
      minWidth: 120,
      type: "number",
      isDecimal: false,
      format: (value) => `Rs: ${parseFloat(value || 0).toFixed(2)}`,
    },
    { id: "isPriceUpdate", label: "Update Material Price", minWidth: 120 },
  ];

  const handleOpenPaymentModal = () => {
    setEnablePayment(true);
  };

  const handleClosePaymentModal = () => {
    setEnablePayment(false);
  };

  const updatePayment = async () => {
    if (payments && payments.length > 0) {
      try {
        const submitBillPayment = await updateImportBillPayments(
          billId,
          payments,
        );
        if (submitBillPayment) {
          console.log("Payment methods updated successfully");
          // Redirect to the bill view page after successful submission
          handleClosePaymentModal();
          fetchData();
        } else {
          // setAlertData({
          //   type: "error",
          //   message: "Something went wrong...",
          // });
          // setAlert(true);
        }
      } catch (error) {
        // console.error("Error submitting payment:", error);
        // setAlertData({
        //   type: "error",
        //   message: "Failed to update payment information",
        // });
        // setAlert(true);
      }
    }
  };

  const formatCurrency = (value) => {
    return typeof value === "number"
      ? `Rs: ${value.toFixed(2)}`
      : `Rs: ${parseFloat(value || 0).toFixed(2)}`;
  };

  const sendingBillForApproval = async () => {
    try {
      await importBillSendingForApproval(billId);
      fetchData(); // Refresh data after approval
    } catch (error) {
      console.error("Error sending bill for approval:", error);
    }
  };

  const approvalForImportBill = async (isApproved) => {
    try {
      await approveImportBill(billId, isApproved);
      fetchData(); // Refresh data after approval
    } catch (error) {
      console.error("Error approving bill:", error);
    }
  };

  const handleDeleteBill = async () => {
    const success = await deleteImportBill(billId);
    if (success) {
      history.goBack();
    }
  };

  const handleForceDelete = async () => {
    setForceDeleteLoading(true);
    const success = await forceDeleteImportBill(billId);
    setForceDeleteLoading(false);
    if (success) {
      setForceDeleteOpen(false);
      history.goBack();
    }
  };

  const handleReturnBillClick = (returnBillId) => {
    history.push("/template/im_bill_return_view/" + returnBillId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Loading bill details...</Typography>
      </Box>
    );
  }

  if (!importBill) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Box sx={{ mb: 2 }}>
          <DescriptionIcon color="error" sx={{ fontSize: 48 }} />
        </Box>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Bill Not Found
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The requested bill data could not be retrieved or does not exist.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  const getStatusChip = (status) => {
    let color;
    let bgColor;

    switch (status?.toUpperCase()) {
      case "DRAFT":
        color = theme.palette.warning.main;
        bgColor = alpha(theme.palette.warning.main, 0.1);
        break;
      case "APPROVED":
        color = theme.palette.success.main;
        bgColor = alpha(theme.palette.success.main, 0.1);
        break;
      case "AWAITING":
        color = theme.palette.info.main;
        bgColor = alpha(theme.palette.info.main, 0.1);
        break;
      case "REJECTED":
        color = theme.palette.error.main;
        bgColor = alpha(theme.palette.error.main, 0.1);
        break;
      default:
        color = theme.palette.grey[600];
        bgColor = alpha(theme.palette.grey[600], 0.1);
    }

    return (
      <Chip
        label={status || "Unknown"}
        size="small"
        sx={{
          bgcolor: bgColor,
          color: color,
          fontWeight: 600,
          borderRadius: 1,
          px: 1,
        }}
      />
    );
  };

  const getTypeChip = (billType) => {
    let color;
    let bgColor;

    switch (billType) {
      case "RETURN BILL":
        color = theme.palette.warning.main;
        bgColor = alpha(theme.palette.warning.main, 0.1);
        break;
      case "INTAKE BILL":
        color = theme.palette.success.main;
        bgColor = alpha(theme.palette.success.main, 0.1);
        break;
      default:
        color = theme.palette.grey[600];
        bgColor = alpha(theme.palette.grey[600], 0.1);
    }

    return (
      <Chip
        label={billType}
        size="small"
        sx={{
          bgcolor: bgColor,
          color: color,
          fontWeight: 600,
          borderRadius: 1,
          px: 1,
        }}
      />
    );
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 2 },
        m: { xs: 1, md: 2 },
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ReceiptIcon fontSize="large" color="primary" />
          <Typography variant="h5" fontWeight="600" color="primary.main">
            Import Bill Details
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={() => history.goBack()}
            aria-label="close"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.grey[500], 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Bill Header Information */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <TagIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Bill Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Bill Number
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {importBill.billNo}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarTodayIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body1">
                      {importBill.createdDate}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Added By
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PersonIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body1">
                      {importBill.addedBy}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Shop
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <StoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body1">{importBill.shop}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  {getStatusChip(importBill.status)}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Bill Type
                  </Typography>
                  {getTypeChip(importBill?.billType)}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <AttachMoneyIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Financial Details
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      color="success.main"
                    >
                      {formatCurrency(importBill.total)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Discount
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <DiscountIcon sx={{ color: "warning.main" }} />
                      <Typography
                        variant="h6"
                        fontWeight="700"
                        color="warning.main"
                      >
                        {formatCurrency(importBill.discount)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bill Note */}
      {importBill.note && (
        <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <DescriptionIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color="primary.main"
              >
                Note
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ pl: 3, mt: 1 }}>
              {importBill.note}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Bill Items Table */}
      <Card elevation={1} sx={{ mb: 4, borderRadius: 2, overflow: "hidden" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <ShoppingBasketIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Bill Items
            </Typography>
            <Badge badgeContent={rows.length} color="primary" sx={{ ml: 1 }} />
          </Box>

          <Box
            sx={{
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <TableItem
              columns={columns}
              rows={rows}
              page={0}
              setPage={() => {}}
              tablePagin={false}
              totalPages={1}
              getData={fetchData}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Payments List */}

      <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <AttachMoneyIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Payment History
            </Typography>
          </Box>
          <Box
            sx={{
              maxWidth: "20%",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            {!enablePayment && (
              <Tooltip
                title={
                  importBill.status !== "APPROVED"
                    ? "Payments can only be added after the bill is approved"
                    : ""
                }
              >
                <span>
                  <SelectButton
                    startIcon={<AddPaymentIcon />}
                    onClick={handleOpenPaymentModal}
                    disabled={importBill.status !== "APPROVED"}
                  >
                    Add Payment
                  </SelectButton>
                </span>
              </Tooltip>
            )}
          </Box>
          {enablePayment ? (
            <PaymentOptions
              paymentMethods={payments}
              setPaymentMethods={setPayments}
              billType={"IMPORT"}
              paymentTypes={[
                { value: "CASH", label: "Cash" },
                { value: "CHEQUE", label: "Cheque" },
                { value: "CREDIT", label: "Credit" },
              ]}
            />
          ) : payments && payments.length > 0 ? (
            <PaymentListForConfimation payments={payments} />
          ) : (
            <Typography color="text.secondary">
              No payments available
            </Typography>
          )}
          {enablePayment ? (
            <Box
              sx={{
                maxWidth: "50%",
                display: "flex",
                marginTop: 2,
                gap: 2,
              }}
            >
              <Stack direction="row" justifyContent="flex" spacing={5}>
                <ActionButton
                  variant="outlined"
                  color="error"
                  startIcon={<CloseIcon />}
                  onClick={handleClosePaymentModal}
                >
                  Cancel
                </ActionButton>
                <ActionButton
                  variant="contained"
                  color="primary"
                  startIcon={<DoneIcon />}
                  onClick={updatePayment}
                >
                  Save Changes
                </ActionButton>
              </Stack>
            </Box>
          ) : null}
          {/* {payments && payments.length > 0 ? (
            <PaymentList payments={payments} />
          ) : (
            <Typography color="text.secondary">
              No payments available
            </Typography>
          )} */}
        </CardContent>
      </Card>

      {/* Return Bill List - Compact & Attractive Design */}
      {returnBillList.length > 0 && (
        <Card elevation={1} sx={{ mb: 4, borderRadius: 2, overflow: "hidden" }}>
          <CardContent sx={{ pb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <AssignmentReturnIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color="primary.main"
              >
                Return Bills
              </Typography>
              <Badge
                badgeContent={returnBillList.length}
                color="primary"
                sx={{ ml: 1 }}
              />
            </Box>

            <Grid container spacing={2}>
              {returnBillList.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.id || index}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      transition: "all 0.2s ease-in-out",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 8px ${alpha(
                          theme.palette.common.black,
                          0.1,
                        )}`,
                      },
                    }}
                    onClick={() => handleReturnBillClick(item.billId)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: alpha(theme.palette.warning.main, 0.2),
                            color: theme.palette.warning.main,
                            mr: 1,
                          }}
                        >
                          <ReceiptIcon sx={{ fontSize: "16px" }} />
                        </Avatar>
                        <Typography variant="body2" fontWeight="600">
                          {item.billNo}
                        </Typography>
                      </Box>
                      <Chip
                        label={formatCurrency(item.total || 0)}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          bgcolor: alpha(theme.palette.warning.main, 0.1),
                          color: theme.palette.warning.main,
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(item.createdDate)}
                      </Typography>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          sx={{
                            p: 0.5,
                            color: theme.palette.primary.main,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                            },
                          }}
                        >
                          <ArrowForwardIosIcon sx={{ fontSize: "12px" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>

        {(importBill.status === "DRAFT" || importBill.status === "REJECTED") && (
          <RoleBasedRender allowedRoles={["IMPORT_BILL_DELETE", "ROLE_ADMIN"]}>
            <Button
              onClick={handleDeleteBill}
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ px: 3 }}
            >
              Delete
            </Button>
          </RoleBasedRender>
        )}

        <RoleBasedRender allowedRoles={["HIGHER_IMPORT_BILL_DELETE", "ROLE_ADMIN"]}>
          <Button
            onClick={() => { setForceDeleteConfirmText(""); setForceDeleteOpen(true); }}
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
            sx={{ px: 3 }}
          >
            Wipe Bill
          </Button>
        </RoleBasedRender>

        {importBill.status === "DRAFT" && (
          <RoleBasedRender allowedRoles={["IMPORT_BILL_CREATE", "ROLE_ADMIN"]}>
            <Button
              onClick={sendingBillForApproval}
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              sx={{ px: 3 }}
            >
              Send for Approval
            </Button>
          </RoleBasedRender>
        )}

        {importBill.status === "AWAITING" && (
          <RoleBasedRender allowedRoles={["IMPORT_BILL_APPROVE", "ROLE_ADMIN"]}>
            <Button
              onClick={approvalForImportBill.bind(this, true)}
              variant="contained"
              color="success"
              startIcon={<DoneIcon />}
              sx={{ px: 3 }}
            >
              Approve
            </Button>
          </RoleBasedRender>
        )}

        {importBill.status === "AWAITING" && (
          <RoleBasedRender allowedRoles={["IMPORT_BILL_APPROVE", "ROLE_ADMIN"]}>
            <Button
              onClick={approvalForImportBill.bind(this, false)}
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              sx={{ px: 3 }}
            >
              Reject
            </Button>
          </RoleBasedRender>
        )}
      </Box>
      {/* Force Delete Confirmation Dialog */}
      <Dialog
        open={forceDeleteOpen}
        onClose={() => setForceDeleteOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "error.main" }}>
          <WarningAmberIcon color="error" />
          Permanently Delete Bill
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action is <strong>irreversible</strong>. The following will be permanently wiped:
          </Alert>
          <Box component="ul" sx={{ mt: 0, pl: 2, "& li": { mb: 0.5 } }}>
            <li>All bill line items</li>
            <li>Stock intake records — inventory will be reverted</li>
            <li>All return bills and their inventory impact</li>
            <li>Price records updated by this bill — previous prices will be restored</li>
            <li>All payment records (cash, cheque, credit)</li>
          </Box>
          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            Type the bill number <strong>{importBill.billNo}</strong> to confirm:
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={importBill.billNo}
            value={forceDeleteConfirmText}
            onChange={(e) => setForceDeleteConfirmText(e.target.value)}
            error={forceDeleteConfirmText.length > 0 && forceDeleteConfirmText !== importBill.billNo}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setForceDeleteOpen(false)} disabled={forceDeleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleForceDelete}
            variant="contained"
            color="error"
            startIcon={forceDeleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteForeverIcon />}
            disabled={forceDeleteConfirmText !== importBill.billNo || forceDeleteLoading}
          >
            {forceDeleteLoading ? "Deleting..." : "Permanently Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

Im_BillView.propTypes = {
  importId: PropTypes.any,
};

export default Im_BillView;
